import {Button, Table} from '@mantine/core'
import type {GitHubApiConnection, GitHubRepository} from '@package/shared'
import {Fragment, useState} from 'react'
import {IconChevronDown, IconChevronRight, IconEyeOff} from '@tabler/icons-react'
import {RepositoryRow} from './RepositoryRow'
import {useConnections} from '../../providers/connections/useConnections.ts'

type Props = {
  connection: GitHubApiConnection
  onClick: (repository: GitHubRepository) => void
}

export const RepositoryRows = ({connection, onClick}: Props) => {
  const {disableRepository} = useConnections()
  const [showDisabled, setShowDisabled] = useState<boolean>(false)
  const [disablingRepositories, setDisablingRepositories] = useState<boolean>(false)

  const enabledRepositories: GitHubRepository[] = connection.repositories.filter(
    (repository) => !repository.disabledTime,
  )
  const enabledRepositoriesWithoutProject = enabledRepositories.filter((repository) => !repository.projectId)
  const disabledRepositories: GitHubRepository[] = connection.repositories.filter(
    (repository) => !!repository.disabledTime,
  )

  const handleDisableAllRepositoriesWithoutProject = (): void => {
    setDisablingRepositories(true)
    Promise.all(
      enabledRepositoriesWithoutProject.map((repository) => disableRepository(connection.id, repository.id)),
    ).finally(() => setDisablingRepositories(false))
  }

  return (
    <Fragment>
      {enabledRepositories.map((repository) => (
        <RepositoryRow key={repository.id} repository={repository} onClick={() => onClick(repository)} />
      ))}

      {enabledRepositoriesWithoutProject.length > 0 && (
        <Table.Tr>
          <Table.Td />
          <Table.Td colSpan={3}>
            <Button
              variant={'subtle'}
              size={'xs'}
              color={'orange'}
              onClick={handleDisableAllRepositoriesWithoutProject}
              fullWidth={false}
              justify={'left'}
              p={5}
              ml={10}
              leftSection={<IconEyeOff stroke={1} size={18} />}
              loading={disablingRepositories}
            >
              Disable {enabledRepositoriesWithoutProject.length}{' '}
              {enabledRepositoriesWithoutProject.length === 1 ? 'repository' : 'repositories'} without assigned project
            </Button>
          </Table.Td>
        </Table.Tr>
      )}

      {disabledRepositories.length > 0 && (
        <Table.Tr>
          <Table.Td />
          <Table.Td colSpan={3}>
            <Button
              variant={'transparent'}
              size={'compact-xs'}
              onClick={() => setShowDisabled((collapsed) => !collapsed)}
              leftSection={showDisabled ? <IconChevronDown stroke={1} /> : <IconChevronRight stroke={1} />}
              fullWidth={false}
              justify={'left'}
              p={0}
              m={0}
              pl={15}
            >
              {showDisabled ? 'Hide ignored repositories' : 'Show ignored repositories'}
            </Button>
          </Table.Td>
        </Table.Tr>
      )}

      {showDisabled &&
        disabledRepositories.map((repository) => (
          <RepositoryRow key={repository.id} repository={repository} onClick={() => onClick(repository)} />
        ))}
    </Fragment>
  )
}
